<script setup lang="ts">
import { computed, h, onMounted, ref, type Ref, type VNode } from 'vue';
import { storeToRefs } from 'pinia';
import { NSpace, NTag, useThemeVars, type DataTableColumns, type TagProps } from 'naive-ui';
import { PlaybookRunStatus, type ReviewProjectAnalysisRunStatistics } from '@/api';
import StatisticsSunburstCard from '@/components/charts/StatisticsSunburstCard.vue';
import { ReviewProjectService } from '@/services/reviewProjectService';
import { useReviewProjectStore } from '@/stores/reviewProjectStore';
import { formatDateTimeForTable, formatDurationFromSeconds } from '@/utils/datetimeUtils';
import {
  buildFindingsSunburst,
  buildMimeTypeSunburst,
  sortedSeverityEntries,
} from '@/utils/reviewProjectStatistics';
import { tagChipColor } from '@/utils/tagColors';

const props = defineProps<{
  projectUid: string;
}>();

const reviewProjectStore = useReviewProjectStore();
const { fileStatistics, seenStatistics, tagStatistics, analysisStatistics, questionStatistics } =
  storeToRefs(reviewProjectStore);
const themeVars = useThemeVars();

// Errors render inline per card (not via toasts): the four fetches run
// concurrently, and four stacked toasts would drown out the sections that did load.
const fileLoading = ref<boolean>(false);
const fileError = ref<string | null>(null);
const seenLoading = ref<boolean>(false);
const seenError = ref<string | null>(null);
const tagLoading = ref<boolean>(false);
const tagError = ref<string | null>(null);
const analysisLoading = ref<boolean>(false);
const analysisError = ref<string | null>(null);
const questionLoading = ref<boolean>(false);
const questionError = ref<string | null>(null);

async function loadSection<T>(
  cached: Ref<T | null>,
  loading: Ref<boolean>,
  error: Ref<string | null>,
  fetcher: () => Promise<T>
): Promise<void> {
  // Refetch on every visit — the numbers drift while others work the project —
  // but keep showing the cached data from a previous mount while doing so; the
  // spinner is for the first load only.
  if (cached.value === null) loading.value = true;
  error.value = null;
  try {
    cached.value = await fetcher();
  } catch (error_) {
    // A failed background refresh keeps the stale card instead of blanking it.
    if (cached.value === null) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to load statistics.';
    }
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  // Deliberately not awaited together: each card renders as soon as its own data arrives.
  void loadSection(fileStatistics, fileLoading, fileError, () =>
    ReviewProjectService.getFileStatistics(props.projectUid)
  );
  void loadSection(seenStatistics, seenLoading, seenError, () =>
    ReviewProjectService.getSeenStatistics(props.projectUid)
  );
  void loadSection(tagStatistics, tagLoading, tagError, () =>
    ReviewProjectService.getTagStatistics(props.projectUid)
  );
  void loadSection(analysisStatistics, analysisLoading, analysisError, () =>
    ReviewProjectService.getAnalysisStatistics(props.projectUid)
  );
  void loadSection(questionStatistics, questionLoading, questionError, () =>
    ReviewProjectService.getQuestionStatistics(props.projectUid)
  );
});

function toPercentage(fraction: number): number {
  return Number.isFinite(fraction) ? Math.round(fraction * 1000) / 10 : 0;
}

// Data room
const categoryEntries = computed(() =>
  Object.entries(fileStatistics.value?.count_by_file_category ?? {}).sort((a, b) => b[1] - a[1])
);

// Tags
interface TagRow {
  key: string;
  name: string;
  chipColor?: ReturnType<typeof tagChipColor>;
  barColor?: string;
  percentage: number;
  fileCount: number;
}

const tagRows = computed<TagRow[]>(() => {
  const stats = tagStatistics.value;
  if (!stats) return [];
  const total = stats.total_file_count;
  const share = (count: number): number =>
    total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
  const rows: TagRow[] = [...(stats.tags ?? [])]
    .sort((a, b) => b.file_count - a.file_count || a.name.localeCompare(b.name))
    .map(tag => ({
      key: tag.tag_uid,
      name: tag.name,
      chipColor: tagChipColor(tag.color),
      barColor: tag.color,
      percentage: share(tag.file_count),
      fileCount: tag.file_count,
    }));
  if (stats.untagged_file_count > 0) {
    rows.push({
      key: 'untagged',
      name: 'Untagged',
      percentage: share(stats.untagged_file_count),
      fileCount: stats.untagged_file_count,
    });
  }
  return rows;
});

// Analyses
const SEVERITY_TAG_TYPE: Record<string, TagProps['type']> = {
  low: 'success',
  medium: 'warning',
  high: 'error',
};

function severityTagType(severity: string): TagProps['type'] {
  return SEVERITY_TAG_TYPE[severity] ?? 'default';
}

const STATUS_ORDER: PlaybookRunStatus[] = [
  PlaybookRunStatus.PENDING,
  PlaybookRunStatus.RUNNING,
  PlaybookRunStatus.SUMMARIZING,
  PlaybookRunStatus.DONE,
  PlaybookRunStatus.FAILED,
  PlaybookRunStatus.PARTIAL,
  PlaybookRunStatus.SKIPPED,
  PlaybookRunStatus.CANCELLED,
  PlaybookRunStatus.WARNING,
];

const STATUS_TAG_TYPE: Record<PlaybookRunStatus, TagProps['type']> = {
  [PlaybookRunStatus.PENDING]: 'default',
  [PlaybookRunStatus.RUNNING]: 'info',
  [PlaybookRunStatus.SUMMARIZING]: 'info',
  [PlaybookRunStatus.DONE]: 'success',
  [PlaybookRunStatus.FAILED]: 'error',
  [PlaybookRunStatus.PARTIAL]: 'warning',
  [PlaybookRunStatus.SKIPPED]: 'default',
  [PlaybookRunStatus.CANCELLED]: 'default',
  [PlaybookRunStatus.WARNING]: 'warning',
};

const totalSeverityEntries = computed(() =>
  sortedSeverityEntries(analysisStatistics.value?.total_finding_count_by_severity).filter(
    ([, count]) => count > 0
  )
);

const totalFindingCount = computed(() =>
  totalSeverityEntries.value.reduce((sum, [, count]) => sum + count, 0)
);

function renderCountTags<K extends string>(
  entries: [K, number][],
  tagType: (key: K) => TagProps['type']
): VNode | string {
  if (!entries.length) return '—';
  return h(NSpace, { size: 4 }, () =>
    entries.map(([key, count]) =>
      h(NTag, { size: 'small', bordered: false, type: tagType(key) }, () => `${count} ${key}`)
    )
  );
}

const analysisColumns: DataTableColumns<ReviewProjectAnalysisRunStatistics> = [
  {
    title: 'Analysis',
    key: 'analysis_name',
    ellipsis: { tooltip: true },
    render: row => row.analysis_name ?? '—',
  },
  { title: 'Playbook', key: 'playbook_name', ellipsis: { tooltip: true } },
  { title: 'Files', key: 'distinct_file_count', width: 70 },
  { title: 'Runs', key: 'total_run_count', width: 70 },
  {
    title: 'Status',
    key: 'run_count_by_status',
    render: row =>
      renderCountTags(
        STATUS_ORDER.map(status => [status, row.run_count_by_status?.[status] ?? 0] as const)
          .filter(([, count]) => count > 0)
          .map(([status, count]): [PlaybookRunStatus, number] => [status, count]),
        status => STATUS_TAG_TYPE[status]
      ),
  },
  {
    title: 'Errors',
    key: 'runs_with_error_count',
    width: 80,
    render: row =>
      row.runs_with_error_count > 0
        ? h('span', { style: 'color: var(--error-color)' }, String(row.runs_with_error_count))
        : '—',
  },
  {
    title: 'Findings',
    key: 'finding_count_by_severity',
    render: row =>
      renderCountTags(
        sortedSeverityEntries(row.finding_count_by_severity).filter(([, count]) => count > 0),
        severityTagType
      ),
  },
];

function analysisRowKey(row: ReviewProjectAnalysisRunStatistics): string {
  return row.analysis_uid ?? row.playbook_uid;
}

// Sunbursts
const severityColors = computed<Record<string, string>>(() => ({
  low: themeVars.value.successColor,
  medium: themeVars.value.warningColor,
  high: themeVars.value.errorColor,
}));

const findingsSunburst = computed(() =>
  analysisStatistics.value
    ? buildFindingsSunburst(analysisStatistics.value, severityColors.value)
    : buildFindingsSunburst({ analyses: [], total_run_count: 0 }, severityColors.value)
);

const mimeTypeSunburst = computed(() =>
  buildMimeTypeSunburst(fileStatistics.value?.count_by_mime_type)
);
</script>

<template>
  <n-grid
    cols="1 s:1 m:2 xl:3"
    responsive="screen"
    data-tour="rp.statistics"
    :x-gap="16"
    :y-gap="16"
  >
    <!-- Data room -->
    <n-gi>
      <n-card title="Data Room">
        <n-space v-if="fileLoading" justify="center"><n-spin /></n-space>
        <n-empty v-else-if="fileError" :description="fileError ?? undefined" />
        <template v-else-if="fileStatistics">
          <n-space align="center" :size="24" :wrap="false">
            <n-progress
              type="circle"
              :percentage="toPercentage(fileStatistics.usable_file_percent)"
            />
            <n-space vertical :size="16">
              <n-statistic label="Total files" :value="fileStatistics.total_file_count" />
              <n-space :size="24">
                <n-statistic label="Usable" :value="fileStatistics.usable_file_count" />
                <n-statistic label="Not usable" :value="fileStatistics.non_usable_file_count" />
                <n-statistic
                  label="Fully reviewed"
                  :value="fileStatistics.fully_reviewed_file_count"
                />
              </n-space>
            </n-space>
          </n-space>
          <n-space v-if="categoryEntries.length" :size="6" style="margin-top: 16px">
            <n-tag v-for="[category, count] in categoryEntries" :key="category" size="small" round>
              {{ category }}: {{ count }}
            </n-tag>
          </n-space>
          <n-space vertical :size="0" style="margin-top: 16px">
            <n-text depth="3">
              First upload: {{ formatDateTimeForTable(fileStatistics.first_file_uploaded_at) }}
            </n-text>
            <n-text depth="3">
              Last upload: {{ formatDateTimeForTable(fileStatistics.last_file_uploaded_at) }}
            </n-text>
          </n-space>
        </template>
      </n-card>
    </n-gi>

    <!-- Review coverage -->
    <n-gi>
      <n-card title="Review Coverage">
        <n-space v-if="seenLoading" justify="center"><n-spin /></n-space>
        <n-empty v-else-if="seenError" :description="seenError ?? undefined" />
        <template v-else-if="seenStatistics">
          <n-space align="center" :size="24" :wrap="false">
            <n-progress
              type="circle"
              status="success"
              :percentage="toPercentage(seenStatistics.seen_page_percent)"
            />
            <n-statistic label="Pages seen" :value="seenStatistics.seen_page_count">
              <template #suffix>/ {{ seenStatistics.total_page_count }}</template>
            </n-statistic>
          </n-space>
          <n-space :size="24" style="margin-top: 16px">
            <n-statistic label="Fully seen" :value="seenStatistics.fully_seen_file_count" />
            <n-statistic label="Partially seen" :value="seenStatistics.partially_seen_file_count" />
            <n-statistic label="Never seen" :value="seenStatistics.never_seen_file_count" />
          </n-space>
        </template>
      </n-card>
    </n-gi>

    <!-- Tags -->
    <n-gi>
      <n-card title="Tags">
        <n-space v-if="tagLoading" justify="center"><n-spin /></n-space>
        <n-empty v-else-if="tagError" :description="tagError ?? undefined" />
        <template v-else-if="tagStatistics">
          <n-empty v-if="!tagRows.length" description="No tags yet" />
          <n-space v-else vertical :size="10">
            <n-flex v-for="row in tagRows" :key="row.key" align="center" :wrap="false" :size="8">
              <n-tag size="small" round :bordered="false" :color="row.chipColor">
                {{ row.name }}
              </n-tag>
              <n-progress
                type="line"
                :percentage="row.percentage"
                :color="row.barColor"
                :show-indicator="false"
                style="flex: 1"
              />
              <n-text depth="3">{{ row.fileCount }}</n-text>
            </n-flex>
          </n-space>
        </template>
      </n-card>
    </n-gi>

    <!-- Questions -->
    <n-gi>
      <n-card title="Questions">
        <n-space v-if="questionLoading" justify="center"><n-spin /></n-space>
        <n-empty v-else-if="questionError" :description="questionError ?? undefined" />
        <template v-else-if="questionStatistics">
          <n-empty
            v-if="questionStatistics.total_question_count === 0"
            description="No questions yet"
          />
          <template v-else>
            <n-space align="center" :size="24" :wrap="false">
              <n-progress
                type="circle"
                :percentage="toPercentage(questionStatistics.answered_question_percent)"
              />
              <n-space vertical :size="16">
                <n-statistic label="Answered" :value="questionStatistics.answered_question_count">
                  <template #suffix>/ {{ questionStatistics.total_question_count }}</template>
                </n-statistic>
                <n-statistic label="Open" :value="questionStatistics.unanswered_question_count" />
              </n-space>
            </n-space>
            <n-text depth="3" style="display: block; margin-top: 16px">
              Avg. time to answer:
              {{ formatDurationFromSeconds(questionStatistics.average_time_to_answer_seconds) }}
            </n-text>
          </template>
        </template>
      </n-card>
    </n-gi>

    <!-- Findings sunburst -->
    <n-gi span="1 m:1 xl:2">
      <n-card v-if="analysisLoading || analysisError" title="Findings by Analysis">
        <n-space v-if="analysisLoading" justify="center"><n-spin /></n-space>
        <n-empty v-else :description="analysisError ?? undefined" />
      </n-card>
      <StatisticsSunburstCard
        v-else
        title="Findings by Analysis"
        :data="findingsSunburst"
        height="360px"
        empty-description="No findings yet"
      />
    </n-gi>

    <!-- File types sunburst -->
    <n-gi>
      <n-card v-if="fileLoading || fileError" title="File Types">
        <n-space v-if="fileLoading" justify="center"><n-spin /></n-space>
        <n-empty v-else :description="fileError ?? undefined" />
      </n-card>
      <StatisticsSunburstCard
        v-else
        title="File Types"
        :data="mimeTypeSunburst"
        height="360px"
        empty-description="No files yet"
      />
    </n-gi>

    <!-- Analyses -->
    <n-gi span="1 m:2 xl:3">
      <n-card title="Analyses">
        <n-space v-if="analysisLoading" justify="center"><n-spin /></n-space>
        <n-empty v-else-if="analysisError" :description="analysisError ?? undefined" />
        <template v-else-if="analysisStatistics">
          <n-space align="center" :size="24">
            <n-statistic label="Total runs" :value="analysisStatistics.total_run_count" />
            <n-statistic label="Total findings" :value="totalFindingCount" />
            <n-space v-if="totalSeverityEntries.length" :size="6">
              <n-tag
                v-for="[severity, count] in totalSeverityEntries"
                :key="severity"
                size="small"
                :bordered="false"
                :type="severityTagType(severity)"
              >
                {{ count }} {{ severity }}
              </n-tag>
            </n-space>
          </n-space>
          <n-data-table
            v-if="analysisStatistics.analyses?.length"
            :columns="analysisColumns"
            :data="analysisStatistics.analyses"
            :row-key="analysisRowKey"
            size="small"
            style="margin-top: 16px"
          />
          <n-empty v-else description="No analyses yet" style="margin-top: 16px" />
        </template>
      </n-card>
    </n-gi>
  </n-grid>
</template>
